import { BaseSubscribable } from "../../subscribable/subscribable";

type Transform<TState, TResult> = {
  execute: () => Promise<TResult>;

  /** transform the state after the promise resolves */
  then?: (state: TState, result: TResult) => TState;

  /** transform the state during resolution and afterwards */
  optimistic?: (state: TState) => TState;

  /** transform the state only while loading */
  loading?: (state: TState, task: Promise<TResult>) => TState;
};

type PendingTransform<TState, TResult> = Transform<TState, TResult> & {
  order: number;
  task: Promise<TResult>;
};

type CompletedOptimistic<TState> = {
  order: number;
  optimistic: (state: TState) => TState;
};

const pipeTransforms = <TState, TExtra>(
  initialState: TState,
  extraParam: TExtra,
  transforms: (((state: TState, extra: TExtra) => TState) | undefined)[],
): TState => {
  return transforms.reduce((state, transform) => {
    return transform?.(state, extraParam) ?? state;
  }, initialState);
};

export class OptimisticState<TState> extends BaseSubscribable {
  private readonly _pendingTransforms: Array<PendingTransform<TState, any>> =
    [];

  /**
   * Completed optimistic callbacks stay applied on top of the base value while
   * any transform is pending, so a state replacement made meanwhile cannot hide
   * them. They are dropped when the last transform settles, so `update()` must
   * be given a state that already contains the completed effects, such as one
   * derived from `baseValue`. While transforms are pending, callbacks apply
   * in invocation order. A transform that settles with `then` has every
   * completed callback replayed over its result, so an earlier-invoked update
   * can win over it.
   *
   * Correctness requirement: `optimistic` callbacks must be idempotent.
   */
  private readonly _completedOptimistics: Array<CompletedOptimistic<TState>> =
    [];

  private _nextTransformOrder = 0;
  private _epoch = 0;

  private _baseValue: TState;
  private _cachedValue: TState;

  public constructor(initialState: TState) {
    super();
    this._baseValue = initialState;
    this._cachedValue = initialState;
  }

  private _updateState(): void {
    const activeTransforms = [
      ...this._pendingTransforms.map((transform) => ({
        order: transform.order,
        apply: (state: TState) =>
          pipeTransforms(state, transform.task, [
            transform.loading,
            transform.optimistic,
          ]),
      })),
      ...this._completedOptimistics.map(({ order, optimistic }) => ({
        order,
        apply: optimistic,
      })),
    ].sort((a, b) => a.order - b.order);

    this._cachedValue = activeTransforms.reduce(
      (state, transform) => transform.apply(state),
      this._baseValue,
    );

    this._notifySubscribers();
  }

  public get baseValue(): TState {
    return this._baseValue;
  }

  public get value(): TState {
    return this._cachedValue;
  }

  public update(state: TState): void {
    this._baseValue = state;
    this._updateState();
  }

  public reset(state: TState): void {
    // In-flight optimisticUpdate calls must not write into the replacement state.
    this._epoch++;
    this._pendingTransforms.length = 0;
    this._completedOptimistics.length = 0;
    this._baseValue = state;
    this._cachedValue = state;
    this._notifySubscribers();
  }

  public async optimisticUpdate<TResult>(
    transform: Transform<TState, TResult>,
  ): Promise<TResult> {
    const epoch = this._epoch;
    const order = this._nextTransformOrder++;
    const task = transform.execute();
    const pendingTransform = {
      ...transform,
      order,
      task,
    };
    try {
      this._pendingTransforms.push(pendingTransform);
      this._updateState();

      const result = await task;
      if (epoch !== this._epoch) return result;
      this._baseValue = pipeTransforms(this._baseValue, result, [
        transform.optimistic,
        transform.then,
      ]);

      // `then` can replace state with a stale snapshot, so replay every
      // completed effect; otherwise replay only newer overlapping effects.
      for (const completed of this._completedOptimistics) {
        if (transform.then || completed.order > pendingTransform.order) {
          this._baseValue = completed.optimistic(this._baseValue);
        }
      }

      if (transform.optimistic) {
        this._completedOptimistics.push({
          order: pendingTransform.order,
          optimistic: transform.optimistic,
        });
        this._completedOptimistics.sort((a, b) => a.order - b.order);
      }

      return result;
    } finally {
      const index = this._pendingTransforms.indexOf(pendingTransform);
      if (index > -1) {
        this._pendingTransforms.splice(index, 1);
      }

      if (this._pendingTransforms.length === 0) {
        this._completedOptimistics.length = 0;
      }

      this._updateState();
    }
  }
}
